import { filterRatings, filterRegions, filterYears } from "@/mocks/categories";

type FilterGroupProps = {
  title: string;
  options: { label: string; value: string }[];
  value: string;
  onSelect: (value: string) => void;
};

function FilterGroup({ title, options, value, onSelect }: FilterGroupProps) {
  return (
    <div>
      <h3 className="mb-2.5 flex items-center gap-2 text-[12px] font-medium tracking-wide text-foreground-400">
        <span className="h-3 w-[3px] rounded-full bg-primary-500"></span>
        {title}
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onSelect(option.value)}
              className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 font-label text-[11.5px] transition-colors ${
                active
                  ? "bg-primary-600 font-medium text-background-50"
                  : "border border-background-300/60 text-foreground-500 hover:border-primary-500/70 hover:text-foreground-800"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

type FilterPanelProps = {
  region: string;
  year: string;
  rating: string;
  resultCount: number;
  onRegionChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onRatingChange: (value: string) => void;
  onReset: () => void;
};

export default function FilterPanel({
  region,
  year,
  rating,
  resultCount,
  onRegionChange,
  onYearChange,
  onRatingChange,
  onReset,
}: FilterPanelProps) {
  const hasFilter = region !== "all" || year !== "all" || rating !== "all";

  return (
    <aside className="rounded-lg border border-background-200/80 bg-background-100 p-4 md:p-5">
      <div className="mb-5 flex items-center justify-between border-b border-background-200/70 pb-3">
        <h2 className="flex items-center gap-2 text-[13px] font-semibold text-foreground-800">
          <i className="ri-filter-3-line text-primary-300"></i>
          筛选条件
        </h2>
        <button
          type="button"
          onClick={onReset}
          disabled={!hasFilter}
          className="cursor-pointer font-label text-[11px] text-foreground-500 transition-colors hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
        >
          重置
        </button>
      </div>

      <div className="flex flex-col gap-5">
        <FilterGroup title="地区" options={filterRegions} value={region} onSelect={onRegionChange} />
        <FilterGroup title="年份" options={filterYears} value={year} onSelect={onYearChange} />
        <FilterGroup title="评分区间" options={filterRatings} value={rating} onSelect={onRatingChange} />
      </div>

      <div className="mt-5 rounded-md border border-background-200 bg-background-50 p-3">
        <p className="font-label text-[10.5px] uppercase tracking-[0.18em] text-foreground-500">Result</p>
        <p className="mt-1 text-[12.5px] text-foreground-700">
          共 <span className="font-semibold text-secondary-300">{resultCount}</span> 部作品符合条件
        </p>
        <p className="mt-1.5 text-[11px] leading-relaxed text-foreground-500">
          筛选仅作用于当前分类，切换分类后会保留已选条件。
        </p>
      </div>
    </aside>
  );
}